/**
 * Device fingerprint utility for anonymous session tracking.
 * Collects browser/device signals and hashes them to create a stable identifier.
 * The fingerprint is hashed client-side for privacy.
 */

import { Sha256, bytesToHex } from './sha256';

const FINGERPRINT_STORAGE_KEY = 'omniscribe_fp';
const FINGERPRINT_VERSION = 'v1';

interface FingerprintComponents {
  userAgent: string;
  language: string;
  languages: string[];
  platform: string;
  hardwareConcurrency: number;
  deviceMemory: number | undefined;
  screenWidth: number;
  screenHeight: number;
  screenDepth: number;
  timezone: string;
  timezoneOffset: number;
  touchSupport: boolean;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  webglVendor: string;
  webglRenderer: string;
  canvas: string;
  audio: string;
}

/**
 * Collects browser/device fingerprint components.
 */
async function collectComponents(): Promise<FingerprintComponents> {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: Array.from(navigator.languages || [navigator.language]),
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as unknown as { deviceMemory?: number }).deviceMemory,
    screenWidth: screen.width,
    screenHeight: screen.height,
    screenDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    webglVendor: getWebGLVendor(),
    webglRenderer: getWebGLRenderer(),
    canvas: getCanvasFingerprint(),
    audio: await getAudioFingerprint(),
  };
}

/**
 * Get WebGL vendor string.
 */
function getWebGLVendor(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
      }
    }
  } catch {
    // Ignore errors
  }
  return '';
}

/**
 * Get WebGL renderer string.
 */
function getWebGLRenderer(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
      }
    }
  } catch {
    // Ignore errors
  }
  return '';
}

/**
 * Get canvas fingerprint.
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Draw some text with specific styling
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('OmniScribe Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('OmniScribe Fingerprint', 4, 17);

    return canvas.toDataURL();
  } catch {
    return '';
  }
}

/**
 * Get audio fingerprint using AudioContext.
 */
async function getAudioFingerprint(): Promise<string> {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

    gainNode.gain.value = 0; // Mute audio
    oscillator.type = 'triangle';
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(0);

    return new Promise((resolve) => {
      setTimeout(() => {
        const fingerprint = analyser.frequencyBinCount.toString();
        oscillator.stop();
        audioContext.close();
        resolve(fingerprint);
      }, 100);
    });
  } catch {
    return '';
  }
}

/**
 * Hash fingerprint components to create the final fingerprint.
 */
function hashComponents(components: FingerprintComponents): string {
  const data = [
    FINGERPRINT_VERSION,
    components.userAgent,
    components.language,
    components.languages.join(','),
    components.platform,
    components.hardwareConcurrency.toString(),
    components.deviceMemory?.toString() || '',
    components.screenWidth.toString(),
    components.screenHeight.toString(),
    components.screenDepth.toString(),
    components.timezone,
    components.timezoneOffset.toString(),
    components.touchSupport.toString(),
    components.cookiesEnabled.toString(),
    components.doNotTrack || '',
    components.webglVendor,
    components.webglRenderer,
    components.canvas.slice(0, 100), // Use first 100 chars of canvas data
    components.audio,
  ].join('|');

  const encoder = new TextEncoder();
  const dataBytes = encoder.encode(data);
  const hasher = new Sha256();
  hasher.update(dataBytes);
  return bytesToHex(hasher.digest());
}

/**
 * Generate or retrieve a stable device fingerprint.
 * Caches the fingerprint in localStorage for consistency.
 */
export async function getFingerprint(): Promise<string> {
  // Check localStorage cache
  const cached = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
  if (cached) {
    return cached;
  }

  // Collect and hash components
  const components = await collectComponents();
  const fingerprint = hashComponents(components);

  // Cache for future use
  localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint);

  return fingerprint;
}

/**
 * Clear the cached fingerprint.
 * Useful for testing or when user explicitly wants to reset.
 */
export function clearFingerprint(): void {
  localStorage.removeItem(FINGERPRINT_STORAGE_KEY);
}

/**
 * Check if a fingerprint is cached.
 */
export function hasCachedFingerprint(): boolean {
  return localStorage.getItem(FINGERPRINT_STORAGE_KEY) !== null;
}
